import { useLazyQuery, useMutation } from '@apollo/client';
import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import {
  CheckoutAppEnum,
  CheckoutOriginPlatforms,
  IAddBillingAddressQueryResult,
  IAddShippingAndBillingAddressesQueryResult,
  ICheckoutCart,
  ICreateAddressQueryResult,
  IGetAddressesQueryResult,
  IListShippingMethodsQueryResult,
  IPickupPoint,
  IShoppingCartProduct,
  ISlotAvailabilitiesQueryResult,
  IUpdateAddressQueryResult,
  ShippingMethodTypes,
  isCheckoutProductError,
} from '@monofront/types';
import { useMachine } from '@monofront/xstate-v4/react';
import logger from '@utils/logger';
import {
  ADD_BILLING_ADDRESS,
  ADD_SHIPPING_AND_BILLING_ADDRESS,
  CREATE_ADDRESS,
  GET_ADDRESSES,
  GET_DROP_OFF_POINTS,
  GET_SLOT_AVAILABILITIES_V2,
  LIST_SHIPPING_METHODS,
  UPDATE_ADDRESS,
} from '@monofront/business/queries';
import {
  checkoutMachine,
  selectAddressMachine,
  addressModalMachine,
  selectShippingMethodMachine,
  IAddressAction,
  selectPickupPointMachine,
  ISelectShippingMethodEvents,
  SELECT_ADDRESS_EXTERNAL_ACTIONS,
  SELECT_ADDRESS_EXTERNAL_SERVICES,
  ISelectShippingMethodContext,
} from '@monofront/business/stateMachines';
import { assign, spawn, StateFrom } from '@monofront/xstate-v4';
import { sendMessageToMobile } from '@utils/mobile';
import {
  IFetchSlotsEvent,
  selectTimeSlotsMachine,
  SELECT_TIME_SLOTS_EXTERNAL_SERVICES,
} from '@monofront/business/stateMachines/checkout/selectShippingMethod/selectTimeSlots';
import {
  useEffectOnce,
  useSelectShippingMethod,
} from '@monofront/business/hooks';
import {
  ICheckoutMachineContext,
  ISelectShippingMethodEvent,
} from '@monofront/business/stateMachines/checkout/types';
import { toast } from 'react-toastify';
import { useTranslations } from 'next-intl';
import {
  ABTEST_UNPRESELECT_SHIPPING_METHOD,
  CHECKOUT_ERRORS,
  CHECKOUT_STATE_INDEXES,
} from '@monofront/business/constants';
import { ADDRESS_MODAL_EXTERNAL_ACTIONS } from '@monofront/business/stateMachines/checkout/selectAddress/addressModal/actions';
import { SELECT_PICKUP_POINT_EXTERNAL_SERVICES } from '@monofront/business/stateMachines/checkout/selectShippingMethod/selectPickupPoint/services';
import { SELECT_PICKUP_POINT_EXTERNAL_ACTIONS } from '@monofront/business/stateMachines/checkout/selectShippingMethod/selectPickupPoint/actions';
import { SELECT_SHIPPING_METHOD_EXTERNAL_ACTIONS } from '@monofront/business/stateMachines/checkout/selectShippingMethod/actions';
import { SELECT_SHIPPING_METHOD_EXTERNAL_SERVICES } from '@monofront/business/stateMachines/checkout/selectShippingMethod/services';
import { CHECKOUT_EXTERNAL_ACTIONS } from '@monofront/business/stateMachines/checkout/actions';
import { CHECKOUT_EXTERNAL_SERVICES } from '@monofront/business/stateMachines/checkout/services';
import getAddressGeolocation from '@monofront/business/utils/mapbox';

import getConfig from 'next/config';
import {
  useCheckoutCartId,
  useEligibility,
  useFeatureIsOn,
} from '@monofront/business/contexts';
import { checkoutCartMachine } from '@monofront/business/stateMachines/checkout/checkoutCart/checkoutCart';
import invariant from 'tiny-invariant';
import {
  CHECKOUT_STATES,
  CHECKOUT_TRANSITIONS,
} from '@monofront/business/stateMachines/checkout';
import { CHECKOUT_CART_MACHINE_ID } from '@monofront/business/stateMachines/checkout/checkoutCart/id';
import { useAuthentication } from '@context/businessContainer';
import { useRouter } from 'next/router';
import { useShoppingCartErrorSetter } from '@hooks/cart/useShoppingCart';
import { useSynchronizeCarts } from '@hooks/businessContainer';
import { isMobileApp } from '@utils/environment';

const {
  publicRuntimeConfig: { mapboxToken },
} = getConfig();

const handleErrorDisplay = (message: string) => () => toast.warning(message);

const CheckoutCartMachineContext = createContext<{
  state: StateFrom<typeof checkoutMachine>;
  startCheckout: (products: Array<IShoppingCartProduct>) => void;
  canStartCheckout: () => boolean;
  canStartOrRestartCheckout: () => boolean;
  isWaitingForCart: boolean;
} | null>(null);

const CheckoutCartMachineState = ({
  children,
  checkoutCart,
}: {
  children: any;
  checkoutCart: ICheckoutCart | null;
}) => {
  const isUnpreselectShippingMethodABTestEnabled = useFeatureIsOn(
    ABTEST_UNPRESELECT_SHIPPING_METHOD,
  );
  const tCheckout = useTranslations('Checkout');
  const tShippingErrors = useTranslations('Shipping.Errors');
  const tErrors = useTranslations('Errors');
  const [getAddresses] = useLazyQuery<IGetAddressesQueryResult>(GET_ADDRESSES, {
    ssr: false,
    fetchPolicy: 'cache-and-network',
  });
  const { address } = useEligibility();
  const [checkoutCartId, setCheckoutCartId] = useCheckoutCartId();
  const { synchronizeCarts } = useSynchronizeCarts();
  const { isUserReady, isCustomerLoading } = useAuthentication();
  const router = useRouter();
  const setCheckoutProductErrors = useShoppingCartErrorSetter();

  const [createAddress] =
    useMutation<ICreateAddressQueryResult>(CREATE_ADDRESS);

  const [addShippingAndBillingAddressesMutation] =
    useMutation<IAddShippingAndBillingAddressesQueryResult>(
      ADD_SHIPPING_AND_BILLING_ADDRESS,
      {
        context: {
          Headers: {
            'Content-Type': 'application/json; charset=utf-8',
          },
        },
      },
    );
  const [updateAddress] =
    useMutation<IUpdateAddressQueryResult>(UPDATE_ADDRESS);

  const [fetchShippingMethods] = useLazyQuery<IListShippingMethodsQueryResult>(
    LIST_SHIPPING_METHODS,
    {
      ssr: false,
      fetchPolicy: 'cache-and-network',
    },
  );

  const postShippingAddress = useCallback(
    (context: ICheckoutMachineContext) => {
      invariant(
        context.checkoutCart,
        'Checkout cart must be present to post shipping address',
      );
      invariant(
        context.shippingAddressSelected && context.billingAddressSelected,
        'Checkout addresses informations must be set to postShippingAddress',
      );

      const optimisticResponse = {
        addShippingAndBillingAddresses: {
          ...context.checkoutCart,
          shippingAddress: {
            ...context.shippingAddressSelected,
          },
          billingAddress: {
            ...context.billingAddressSelected,
          },
          __typename: 'CartV2',
        },
      };

      return addShippingAndBillingAddressesMutation({
        variables: {
          tokenValue: context.checkoutCart.id,
          shippingAddress: {
            firstName: context.shippingAddressSelected.firstName,
            lastName: context.shippingAddressSelected.lastName,
            city: context.shippingAddressSelected.city,
            countryCode: context.shippingAddressSelected.countryCode,
            postcode: context.shippingAddressSelected.postcode,
            street: context.shippingAddressSelected.street,
            company: context.shippingAddressSelected.company,
            provinceCode: context.shippingAddressSelected.provinceCode,
            phoneNumber: context.shippingAddressSelected.phoneNumber,
            deliveryInstruction:
              context.shippingAddressSelected.deliveryInstruction,
          },
          billingAddress: {
            firstName: context.billingAddressSelected.firstName,
            lastName: context.billingAddressSelected.lastName,
            city: context.billingAddressSelected.city,
            countryCode: context.billingAddressSelected.countryCode,
            postcode: context.billingAddressSelected.postcode,
            street: context.billingAddressSelected.street,
            company: context.billingAddressSelected.company,
            provinceCode: context.billingAddressSelected.provinceCode,
            phoneNumber: context.billingAddressSelected.phoneNumber,
            deliveryInstruction:
              context.billingAddressSelected.deliveryInstruction,
          },
        },
        optimisticResponse,
      });
    },
    [addShippingAndBillingAddressesMutation],
  );

  const postShippingError = useCallback(
    handleErrorDisplay(tCheckout('addressSubmitError')),
    [],
  );
  const postBillingError = useCallback(
    handleErrorDisplay(tCheckout('addressSubmitError')),
    [],
  );
  const createAddressError = useCallback(
    handleErrorDisplay(tShippingErrors('addAddress')),
    [],
  );
  const updateAddressError = useCallback(
    handleErrorDisplay(tShippingErrors('updateAddress')),
    [],
  );

  const [addBillingAddressMutation] =
    useMutation<IAddBillingAddressQueryResult>(ADD_BILLING_ADDRESS, {
      context: {
        Headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      },
    });

  const postBillingAddress = useCallback(
    (context: ICheckoutMachineContext) => {
      invariant(
        context.checkoutCart,
        'Checkout cart must be present to post shipping address',
      );
      invariant(
        context.billingAddressSelected,
        'Checkout addresses informations must be set to postShippingAddress',
      );

      const optimisticResponse = context.checkoutCart
        ? {
            addBillingAddress: {
              ...context.checkoutCart,
              billingAddress: {
                ...context.billingAddressSelected,
                __typename: 'AddressCartV2',
              },
              __typename: 'CartV2',
            },
          }
        : undefined;

      return addBillingAddressMutation({
        variables: {
          tokenValue: context.checkoutCart.id,
          billingAddress: {
            firstName: context.billingAddressSelected.firstName,
            lastName: context.billingAddressSelected.lastName,
            city: context.billingAddressSelected.city,
            countryCode: context.billingAddressSelected.countryCode,
            postcode: context.billingAddressSelected.postcode,
            street: context.billingAddressSelected.street,
            company: context.billingAddressSelected.company,
            provinceCode: context.billingAddressSelected.provinceCode,
            phoneNumber: context.billingAddressSelected.phoneNumber,
            deliveryInstruction:
              context.billingAddressSelected.deliveryInstruction,
          },
        },
        optimisticResponse,
      });
    },
    [addBillingAddressMutation],
  );

  const selectAddress = useMemo(() => {
    return selectAddressMachine
      .withContext({
        ...selectAddressMachine.initialState.context,
        isUserReady,
        shippingAddressSelected: checkoutCart?.shippingAddress ?? null,
        billingAddressSelected: checkoutCart?.billingAddress ?? null,
        addressPrefilled: address,
        isBillingAndShippingEqual:
          checkoutCart?.billingAddress?.hash ===
          checkoutCart?.shippingAddress?.hash,
      })
      .withConfig({
        actions: {
          spawnMachines: assign({
            addressModalRef: () => {
              return spawn(
                addressModalMachine.withConfig({
                  actions: {
                    [ADDRESS_MODAL_EXTERNAL_ACTIONS.trackOpenModal]: () => {
                      sendMessageToMobile('modal', { isOpen: true });
                    },
                    [ADDRESS_MODAL_EXTERNAL_ACTIONS.trackCloseModal]: () => {
                      sendMessageToMobile('modal', { isOpen: false });
                    },
                  },
                }),
                { sync: true },
              );
            },
          }),
          [SELECT_ADDRESS_EXTERNAL_ACTIONS.createAddressError]:
            createAddressError,
          [SELECT_ADDRESS_EXTERNAL_ACTIONS.updateAddressError]:
            updateAddressError,
        } as any,
        services: {
          [SELECT_ADDRESS_EXTERNAL_SERVICES.createAddress]: (
            _,
            event: IAddressAction,
          ) => createAddress({ variables: { address: event.address } }),
          [SELECT_ADDRESS_EXTERNAL_SERVICES.updateAddress]: (
            _,
            event: IAddressAction,
          ) => updateAddress({ variables: { address: event.address } }),
          [SELECT_ADDRESS_EXTERNAL_SERVICES.getAddresses]: async () => {
            const addresses = await getAddresses();

            return [...(addresses.data?.getAddresses ?? [])]?.sort((a, b) => {
              const defaultAddress = a.default ? 1 : 0;
              const comparedAddress = b.default ? 1 : 0;

              return comparedAddress - defaultAddress;
            });
          },
        } as any,
      });
  }, [
    checkoutCart,
    address,
    isUserReady,
    getAddresses,
    createAddressError,
    updateAddressError,
    createAddress,
    updateAddress,
  ]);

  const [getPickupList] = useLazyQuery<{
    getDropOffPoints: IPickupPoint[];
  }>(GET_DROP_OFF_POINTS, {
    ssr: false,
  });

  const firstShipment = checkoutCart?.shipments?.[0];

  const selectPickupPoint = selectPickupPointMachine
    .withContext({
      ...selectPickupPointMachine.initialState.context,
      pickupAddress: firstShipment?.address ?? null,
    })
    .withConfig({
      services: {
        [SELECT_PICKUP_POINT_EXTERNAL_SERVICES.listPickupPoints]: async (
          context,
          event,
        ) => {
          try {
            const [lng, lat] = await getAddressGeolocation(
              {
                mapboxToken,
                address: `${event.address.street} ${event.address.postcode} ${event.address.city}`,
                countryCode: event.address.countryCode,
              },
              logger,
            );

            const { data: pickupList, error } = await getPickupList({
              variables: {
                city: event.address.city,
                zip: event.address.postcode,
                country: event.address.countryCode,
                limit: 20,
                couriers: (event.methods || context.shippingMethods).map(
                  ({ tmsCode }) => tmsCode,
                ),
                lat,
                lng,
              },
            });

            if (error) throw error;

            return {
              pickupList: pickupList?.getDropOffPoints,
              homePosition: { longitude: lng, latitude: lat },
            };
          } catch (error: any) {
            let errorMessage = tShippingErrors('addressVerification');

            if (error?.graphQLErrors?.length) {
              logger.error(
                `${error.graphQLErrors[0].code} - ${error.graphQLErrors[0].message}`,
                error,
              );

              errorMessage =
                error.graphQLErrors[0].code === 'DROP_OFF_POINT_GET_ERROR'
                  ? tShippingErrors('getRelayPoint')
                  : errorMessage;
            } else {
              logger.error(`${error}`);
            }

            handleErrorDisplay(errorMessage)();
            throw Error(`${error}`);
          }
        },
      } as any,
      actions: {
        [SELECT_PICKUP_POINT_EXTERNAL_ACTIONS.trackOpenModal]: () => {
          sendMessageToMobile('modal', { isOpen: true });
        },
        [SELECT_PICKUP_POINT_EXTERNAL_ACTIONS.trackCloseModal]: () => {
          sendMessageToMobile('modal', { isOpen: false });
        },
      },
    });

  const [getSlotAvailabilitiesV2] =
    useLazyQuery<ISlotAvailabilitiesQueryResult>(GET_SLOT_AVAILABILITIES_V2);

  const selectTimeSlots = selectTimeSlotsMachine
    .withContext({
      ...selectTimeSlotsMachine.initialState.context,
      daySelected: firstShipment?.deliveryDate ?? null,
      hourSelected: firstShipment?.timeSlot ?? null,
      timeSlotId: firstShipment?.timeSlotId ?? null,
      deliveryInfos: firstShipment?.deliveryInstruction ?? null,
    })
    .withConfig({
      services: {
        [SELECT_TIME_SLOTS_EXTERNAL_SERVICES.listTimeSlots]: (_, event) => {
          return getSlotAvailabilitiesV2({
            variables: {
              checkoutToken: (event as IFetchSlotsEvent).checkoutCartId,
              carrierMethod: (event as IFetchSlotsEvent).carrierMethod,
              isFresh: (event as IFetchSlotsEvent).isFresh,
              isUltraFresh: (event as IFetchSlotsEvent).isUltraFresh,
            },
          });
        },
      },
    });

  const fetchShippingMethodsError = handleErrorDisplay(
    tShippingErrors('getDeliveryMethods'),
  );

  const isNonAddressed =
    (firstShipment?.type as ShippingMethodTypes) ===
    ShippingMethodTypes.NON_ADDRESSED;

  const selectShippingMethod = selectShippingMethodMachine
    .withContext({
      ...selectShippingMethodMachine.initialState.context,
      methodSelectedCode: isNonAddressed ? null : firstShipment?.code ?? null,
      methodType: isNonAddressed
        ? null
        : (firstShipment?.type as ShippingMethodTypes),
      checkoutCartId: checkoutCart?.id ?? null,
      isFreshCart: Boolean(checkoutCart?.isFresh),
      isUltraFreshCart: Boolean(checkoutCart?.hasUltraFreshProducts),
      shippingMethod: {
        ...selectShippingMethodMachine.initialState.context.shippingMethod,
        shipmentMethodCode: checkoutCart
          ? CHECKOUT_STATE_INDEXES[checkoutCart.checkoutState] >=
            CHECKOUT_STATE_INDEXES.addressed
            ? firstShipment?.code ?? null
            : null
          : null,
        deliveryDate: firstShipment?.deliveryDate ?? undefined,
        timeSlot: firstShipment?.timeSlot ?? undefined,
        deliveryInstruction:
          firstShipment?.deliveryInstruction ??
          checkoutCart?.shippingAddress?.deliveryInstruction ??
          undefined,
        pointId: firstShipment?.pointId ?? undefined,
        address: firstShipment?.address ?? undefined,
      },
      isUnpreselectShippingMethodABTestEnabled,
    })
    .withConfig({
      services: {
        [SELECT_SHIPPING_METHOD_EXTERNAL_SERVICES.fetchShippingMethods]: (
          context,
        ) =>
          fetchShippingMethods({
            variables: {
              tokenValue: context.checkoutCartId,
            },
          }),
      },
      actions: {
        [SELECT_SHIPPING_METHOD_EXTERNAL_ACTIONS.spawnMachines]: assign<
          ISelectShippingMethodContext,
          ISelectShippingMethodEvents
        >({
          selectPickupPointRef: () => spawn(selectPickupPoint, { sync: true }),
          selectTimeSlotsRef: () => spawn(selectTimeSlots, { sync: true }),
        }),
        [SELECT_SHIPPING_METHOD_EXTERNAL_ACTIONS.fetchShippingMethodsError]:
          fetchShippingMethodsError,
      },
    });

  const postShippingMethod = useSelectShippingMethod();

  const postShippingMethodError = handleErrorDisplay(
    tShippingErrors('selectDeliveryMethod'),
  );

  const syncCart = useCallback(
    (products: Array<IShoppingCartProduct> | null) => {
      return synchronizeCarts(products ?? [], true, {
        checkoutOrigin: isMobileApp
          ? CheckoutAppEnum.MOBILE
          : CheckoutAppEnum.WEB,
        checkoutOriginPlatform: isMobileApp
          ? window._lfMobilePlatform ?? CheckoutOriginPlatforms.WEB
          : CheckoutOriginPlatforms.WEB,
      });
    },
    [synchronizeCarts],
  );

  const checkoutCartMachineConfigured = checkoutCartMachine.withConfig({
    services: {
      [CHECKOUT_EXTERNAL_SERVICES.syncCart]: ({ products }) =>
        syncCart(products),
      [CHECKOUT_EXTERNAL_SERVICES.refreshCart]: () =>
        Promise.resolve({ cart: checkoutCart }),
    },
  });

  const machine = useMemo(() => {
    return checkoutMachine.withConfig({
      actions: {
        [CHECKOUT_EXTERNAL_ACTIONS.syncedCart]: (
          context: ICheckoutMachineContext,
        ) => {
          invariant(
            context.checkoutCart,
            'Machine context should have a checkoutCart when calling syncedCart',
          );

          setCheckoutCartId(context.checkoutCart.id);
        },
        [CHECKOUT_EXTERNAL_ACTIONS.postShippingError]: postShippingError,
        [CHECKOUT_EXTERNAL_ACTIONS.postBillingError]: postBillingError,
        [CHECKOUT_EXTERNAL_ACTIONS.postShippingMethodError]:
          postShippingMethodError,
      } as any,
      services: {
        [CHECKOUT_EXTERNAL_SERVICES.postShippingAddress]: postShippingAddress,
        [CHECKOUT_EXTERNAL_SERVICES.postBillingAddress]: postBillingAddress,
        [CHECKOUT_EXTERNAL_SERVICES.postShippingMethod]: (context, event) => {
          const selectedAddressContext =
            context.selectAddressesRef!.getSnapshot();
          const addressId = selectedAddressContext?.context.addresses.find(
            ({ hash }) => hash === context!.checkoutCart!.shippingAddress?.hash,
          )?.id;

          return postShippingMethod(
            (event as ISelectShippingMethodEvent).methodType,
            {
              shipmentId: context!.checkoutCart!.shipments[0].id,
              tokenValue: context!.checkoutCart!.id,
              addressId: context.shippingAddressSelected?.id ?? addressId,
              ...(event as ISelectShippingMethodEvent).shippingMethod,
              deliveryInstruction:
                (event as ISelectShippingMethodEvent).shippingMethod
                  .deliveryInstruction ??
                context!.checkoutCart!.shippingAddress?.deliveryInstruction ??
                undefined,
            },
          );
        },
      },
    });
  }, []);

  const [state, send] = useMachine(
    machine,
    useMemo(() => {
      return {
        actions: {
          [CHECKOUT_EXTERNAL_ACTIONS.spawnMachines]: assign({
            selectAddressesRef: () => spawn(selectAddress, { sync: true }),
            selectShippingMethodRef: () =>
              spawn(selectShippingMethod, { sync: true }),
          }),
          [CHECKOUT_EXTERNAL_ACTIONS.syncCart]: assign({
            checkoutCartMachineRef: () => {
              return spawn(
                checkoutCartMachineConfigured.withContext({
                  isUserReady,
                  checkoutCart,
                  checkoutCartId: checkoutCart?.id ?? null,
                  errors: null,
                  products: null,
                }),
                { name: CHECKOUT_CART_MACHINE_ID },
              );
            },
          }),
          [CHECKOUT_EXTERNAL_ACTIONS.syncCartError]: (_, event) => {
            invariant(event.type === CHECKOUT_TRANSITIONS.CART_SYNC_DONE);

            if (!('data' in event) || !('errors' in event.data)) {
              return;
            }

            const errors = event.data.errors;

            const isProductError = (error: unknown) =>
              isCheckoutProductError(error) &&
              (error.error === CHECKOUT_ERRORS.STOCK_IS_INSUFFICIENT ||
                error.error === CHECKOUT_ERRORS.PRODUCT_VARIANT_NOT_EXIST ||
                error.error === CHECKOUT_ERRORS.PRODUCT_NOT_EXIST);

            if (errors?.some(isProductError)) {
              setCheckoutProductErrors(errors.filter(isCheckoutProductError));

              router.push({
                pathname: '/cart',
                query: { isCheckoutProductErrorModalOpen: true },
              });
            } else {
              toast(tErrors('redirectionError'), {
                progressClassName: 'toast-error',
              });

              router.push({
                pathname: '/cart',
              });
            }
          },
        },
        context: {
          isUserReady,
          checkoutCartId: checkoutCart?.id ?? null,
          checkoutCart,
        },
        devTools: true,
      };
    }, [checkoutCart, selectAddress]),
  );
  const initialUserReadyOrHandled = useRef(isUserReady);

  const isStateWaitingForAuth = state.can({
    type: CHECKOUT_TRANSITIONS.ENABLE_CHECKOUT,
    isUserReady,
  });

  useEffectOnce(
    () => !isCustomerLoading && isStateWaitingForAuth,
    () => {
      send({ type: CHECKOUT_TRANSITIONS.ENABLE_CHECKOUT, isUserReady });
      initialUserReadyOrHandled.current = isUserReady;
    },
    [isUserReady],
  );

  useEffect(() => {
    if (isUserReady && isUserReady !== initialUserReadyOrHandled.current) {
      send({ type: CHECKOUT_TRANSITIONS.USER_READY, isUserReady });

      initialUserReadyOrHandled.current = isUserReady;
    }
  }, [send, isUserReady]);

  const startCheckout = useCallback(
    (products: Array<IShoppingCartProduct>) => {
      if (state.can({ type: CHECKOUT_TRANSITIONS.RESTART, products: [] })) {
        send(CHECKOUT_TRANSITIONS.RESTART, { products });
      }

      send({
        type: CHECKOUT_TRANSITIONS.START_CHECKOUT,
        products,
        checkoutCartId: checkoutCartId ?? null,
      });
    },
    [state, send, checkoutCartId],
  );

  const canStartCheckout = useCallback(() => {
    return state.can({
      type: CHECKOUT_TRANSITIONS.START_CHECKOUT,
      products: [],
      checkoutCartId: null,
    });
  }, [state]);

  const canStartOrRestartCheckout = useCallback(() => {
    return (
      state.can({
        type: CHECKOUT_TRANSITIONS.START_CHECKOUT,
        products: [],
        checkoutCartId: null,
      }) || state.can({ type: CHECKOUT_TRANSITIONS.RESTART, products: [] })
    );
  }, [state]);

  const isWaitingForCart = useMemo(() => {
    return state.matches(CHECKOUT_STATES.WAITING_CART);
  }, [state]);

  return (
    <CheckoutCartMachineContext.Provider
      value={useMemo(() => {
        return {
          state,
          startCheckout,
          canStartCheckout,
          canStartOrRestartCheckout,
          isWaitingForCart,
        };
      }, [
        state,
        startCheckout,
        canStartCheckout,
        canStartOrRestartCheckout,
        isWaitingForCart,
      ])}
    >
      {children}
    </CheckoutCartMachineContext.Provider>
  );
};

export const useCheckoutCartMachine = () => {
  const ctx = useContext(CheckoutCartMachineContext);

  invariant(ctx, 'CheckoutCartMachine context provider is missing');

  return ctx;
};

export default CheckoutCartMachineState;
